import { z } from 'zod';
import type { CategoriaGasto } from '../estados';
import { dia, textoRequerido, uuid } from './comunes';

export const BOVEDAS = ['efectivo', 'banco'] as const;
export type Boveda = (typeof BOVEDAS)[number];
export const bovedaSchema = z.enum(BOVEDAS);

export const TIPOS_MOVIMIENTO_BOVEDA = ['saldo_inicial', 'ingreso_cierre', 'ingreso', 'retiro', 'traspaso_salida', 'traspaso_entrada', 'correccion_compra'] as const;
export type TipoMovimientoBoveda = (typeof TIPOS_MOVIMIENTO_BOVEDA)[number];

export const MOTIVOS_RETIRO = ['pago_empleados', 'renta', 'compra_producto', 'herramienta', 'ferreteria'] as const;
export type MotivoRetiro = (typeof MOTIVOS_RETIRO)[number];

export const ETIQUETAS_MOTIVO: Record<MotivoRetiro, string> = {
  pago_empleados: 'Pago de empleados',
  renta: 'Pago de renta',
  compra_producto: 'Compra de producto',
  herramienta: 'Compra de herramienta de trabajo',
  ferreteria: 'Compra de ferretería',
};

export const CATEGORIA_POR_MOTIVO: Record<MotivoRetiro, CategoriaGasto> = {
  pago_empleados: 'sueldos',
  renta: 'renta',
  compra_producto: 'compra_productos',
  herramienta: 'herramientas',
  ferreteria: 'ferreteria',
};

const moneda = z.enum(['MXN', 'USD']).default('MXN');
export const montoBoveda = z.number().int().min(1, 'El monto debe ser mayor a cero').max(100_000_000, 'El monto excede el límite permitido');
const soloPesosEnBanco = <T extends { boveda: Boveda; moneda: 'MXN' | 'USD' }>(v: T) => !(v.boveda === 'banco' && v.moneda === 'USD');
const mensajeBanco = { message: 'El banco solo maneja pesos', path: ['moneda'] };

const ingresoBase = z.object({ boveda: bovedaSchema, moneda, monto: montoBoveda });

export const ingresoBovedaSchema = ingresoBase.extend({ concepto: z.string().trim().max(200).optional() }).refine(soloPesosEnBanco, mensajeBanco);
export type IngresoBovedaInput = z.infer<typeof ingresoBovedaSchema>;

export const ingresoCierreSchema = z.object({ turnoId: uuid, ingresos: z.array(ingresoBase.refine(soloPesosEnBanco, mensajeBanco)).min(1).max(4) });
export type IngresoCierreInput = z.infer<typeof ingresoCierreSchema>;

export const retiroBovedaSchema = z
  .object({ boveda: bovedaSchema, moneda, monto: montoBoveda, motivo: textoRequerido(100), concepto: textoRequerido(200) })
  .refine(soloPesosEnBanco, mensajeBanco);
export type RetiroBovedaInput = z.infer<typeof retiroBovedaSchema>;

/** Traspaso entre bóvedas, siempre en pesos; el destino es la otra bóveda. */
export const traspasoSchema = z.object({
  de: bovedaSchema,
  monto: montoBoveda,
  moneda: z.literal('MXN', { message: 'Los traspasos solo son en pesos' }).optional(),
  concepto: z.string().trim().max(200).optional(),
});
export type TraspasoInput = z.infer<typeof traspasoSchema>;

export const saldoInicialSchema = z
  .object({ boveda: bovedaSchema, moneda, monto: z.number().int().min(0).max(1_000_000_000) })
  .refine(soloPesosEnBanco, mensajeBanco);
export type SaldoInicialInput = z.infer<typeof saldoInicialSchema>;

export const POR_PAGINA_BOVEDA = 50;
export const historialBovedaQuery = z.object({
  boveda: bovedaSchema.optional(),
  tipo: z.enum(['entrada', 'salida', 'traspaso']).optional(),
  desde: dia.optional(),
  hasta: dia.optional(),
  pagina: z.coerce.number().int().min(1).default(1),
});
export type HistorialBovedaQuery = z.infer<typeof historialBovedaQuery>;

export interface SaldosBoveda {
  efectivo: { MXN: number; USD: number };
  banco: { MXN: number };
  pendientesSaldoInicial: { boveda: Boveda; moneda: 'MXN' | 'USD' }[];
}

export interface MovimientoBovedaVista {
  id: string;
  fecha: string;
  boveda: Boveda;
  moneda: 'MXN' | 'USD';
  tipo: TipoMovimientoBoveda;
  motivo: MotivoRetiro | null;
  /** Motivo escrito a mano en un retiro manual. */
  motivoLibre: string | null;
  concepto: string | null;
  usuario: string;
  monto: number;
  /** Saldo de esa bóveda y moneda después del movimiento. */
  saldo: number;
  referencia: string | null;
}

export interface HistorialBoveda {
  items: MovimientoBovedaVista[];
  total: number;
  pagina: number;
}

export interface GastoAnteriorVista {
  id: string;
  fecha: string;
  categoria: CategoriaGasto;
  descripcion: string;
  moneda: 'MXN' | 'USD';
  monto: number;
  montoMXN: number;
  origen: string;
}
